/**
 * @fileoverview Print jobs store (Zustand)
 *
 * Backends with the print queue turn every ticket into a job with a real status:
 * queued (not pushed yet) → sent (pushed, not confirmed) → acked (printed) or
 * failed (the print app could not print) / expired (never printed).
 *
 * Sources: the directPrint responses, the `print::job` websocket event of the admin
 * room, and GET /print/jobs on load. The order card reads jobsForOrder().
 *
 * capabilities (GET /print/capabilities): null while unknown or with an old backend.
 *   server_auto_print → the API sends the automatic kitchen tickets itself: this
 *   dashboard must not send its own.
 *
 * @module stores/usePrintJobStore
 */
import { create } from 'zustand';
import { getPrintCapabilities, getPrintJobs, retryPrintJob, onPrintJob } from '@/api';

const CAPABILITIES_TTL_MS = 5 * 60 * 1000;
const MAX_JOBS = 500;

const merge = (current, incoming) => {
  if (!incoming?.job_id) return current;
  const previous = current[incoming.job_id] || {};
  // Never let an older partial record (e.g. the HTTP answer arriving after the
  // websocket "acked") move a job back.
  const RANK = { queued: 0, sent: 1, failed: 2, expired: 2, acked: 3 };
  const keepStatus = previous.status && incoming.status
    && (RANK[previous.status] ?? 0) > (RANK[incoming.status] ?? 0)
    && !(incoming.status === 'sent' && (incoming.attempts || 0) > (previous.attempts || 0));
  const next = {
    ...previous,
    ...Object.fromEntries(Object.entries(incoming).filter(([, v]) => v !== undefined && v !== null)),
  };
  if (keepStatus) next.status = previous.status;
  const jobs = { ...current, [incoming.job_id]: next };
  const ids = Object.keys(jobs);
  if (ids.length > MAX_JOBS) {
    ids
      .sort((a, b) => String(jobs[a].created_at || '').localeCompare(String(jobs[b].created_at || '')))
      .slice(0, ids.length - MAX_JOBS)
      .forEach((id) => { delete jobs[id]; });
  }
  return jobs;
};

export const usePrintJobStore = create((set, get) => ({
  capabilities: null,
  _capabilitiesAt: 0,
  _capabilitiesPromise: null,
  jobs: {},

  /** Cached; resolves to null with a backend that has no print queue. */
  loadCapabilities: async (router, { force = false } = {}) => {
    const { _capabilitiesAt, _capabilitiesPromise, capabilities } = get();
    if (!force && _capabilitiesAt && Date.now() - _capabilitiesAt < CAPABILITIES_TTL_MS) return capabilities;
    if (_capabilitiesPromise) return _capabilitiesPromise;
    const promise = getPrintCapabilities(router)
      .then((caps) => {
        set({ capabilities: caps || null, _capabilitiesAt: Date.now(), _capabilitiesPromise: null });
        return caps || null;
      })
      .catch(() => {
        // Unknown (network): keep the previous answer, retry on the next call.
        set({ _capabilitiesPromise: null });
        return get().capabilities;
      });
    set({ _capabilitiesPromise: promise });
    return promise;
  },

  upsert: (job) => set((state) => ({ jobs: merge(state.jobs, job) })),

  loadRecent: async (router) => {
    try {
      const jobs = await getPrintJobs(router, { minutes: 240 });
      set((state) => ({ jobs: jobs.reduce(merge, state.jobs) }));
    } catch {
      /* status is a convenience: never block the board */
    }
  },

  retry: async (router, jobId) => {
    const job = await retryPrintJob(router, jobId);
    get().upsert(job);
    return job;
  },

  reset: () => set({ jobs: {}, capabilities: null, _capabilitiesAt: 0, _capabilitiesPromise: null }),
}));

// Every directPrint answer with a job id lands here.
onPrintJob((job) => usePrintJobStore.getState().upsert(job));

/**
 * Jobs of one order, newest first. A plain function: components select the stable
 * `state.jobs` and derive with useMemo (a selector returning a new array on every
 * call would re-render forever).
 */
export const jobsForOrder = (jobs, orderId) => {
  if (orderId == null || !jobs) return [];
  return Object.values(jobs)
    .filter((j) => String(j.order_id) === String(orderId))
    .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
};

/** True when the API sends the automatic kitchen tickets for this restaurant. */
export const serverAutoPrint = () => usePrintJobStore.getState().capabilities?.server_auto_print === true;
